import React, { useState, useMemo, useContext } from 'react';
import { Layout } from './Layout';
import { MetricCard } from './MetricCard';
import { BatchFilters } from './BatchFilters';
import { BatchTable } from './BatchTable';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Home, CheckCircle, X, SkipForward, RefreshCw, Clock, TrendingUp, Target, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { AppContext } from '../src/App';

export interface BatchItem {
  id: string;
  clientId: string;
  clientName: string;
  clinicianId: string;
  clinicianName: string;
  visitDate: string;
  goalSummary: string;
  goalType: string;
  aiSuggestion: 'Compliant' | 'Deficient';
  confidence: number;
  status: 'Pending' | 'Auto-Approved' | 'Auto-Rejected' | 'Approved' | 'Rejected' | 'Skipped';
  lastModified: string;
  reviewNotes?: string;
  expandedContent?: string;
  auditorName: string;
}

export interface BatchFilters {
  confidenceMin: number;
  confidenceMax: number;
  clinician: string;
  auditor: string;
  status: string[];
  aiSuggestion: string[];
  searchQuery: string;
}

// Generate mock batch approval data
const generateMockBatchData = (): BatchItem[] => {
  const clients = [
    { id: 'PT10234', name: 'John Anderson' },
    { id: 'PT10235', name: 'Sarah Johnson' },
    { id: 'PT10236', name: 'Michael Chen' },
    { id: 'PT10237', name: 'Emily Rodriguez' },
    { id: 'PT10238', name: 'David Thompson' },
    { id: 'PT10239', name: 'Lisa Williams' },
    { id: 'PT10240', name: 'Robert Davis' },
    { id: 'PT10241', name: 'Jennifer Wilson' },
    { id: 'PT10242', name: 'Christopher Lee' },
    { id: 'PT10243', name: 'Amanda Martinez' }
  ];

  const clinicians = [
    { id: 'DR1001', name: 'Dr. Sarah Mitchell' },
    { id: 'DR1002', name: 'Dr. James Park' },
    { id: 'NP2001', name: 'NP Mary Johnson' },
    { id: 'DR1003', name: 'Dr. Kevin Brown' },
    { id: 'NP2002', name: 'NP Lisa Chen' }
  ];

  const auditors = [
    'John Smith',
    'Sarah Johnson', 
    'Mike Chen',
    'Lisa Davis',
    'Dr. Brown',
    'Dr. Emily Martinez',
    'Dr. James Park',
    'NP Mary Johnson',
    'Dr. Kevin Brown',
    'NP Lisa Chen'
  ];

  const goalTypes = [
    'Medication Management',
    'Care Coordination',
    'Client Education',
    'Follow-up Scheduling',
    'Diagnostic Testing',
    'Lifestyle Counseling',
    'Symptom Monitoring',
    'Preventive Care'
  ];

  const goalSummaries = [
    'Document client\'s understanding of diabetes management',
    'Ensure medication adherence and monitor side effects',
    'Schedule follow-up appointment within 2 weeks',
    'Coordinate care with specialist for treatment planning',
    'Provide client education on hypertension management',
    'Monitor wound healing progress and infection signs',
    'Assess pain levels and adjust medication accordingly',
    'Review lab results and adjust treatment plan',
    'Evaluate treatment effectiveness and client progress',
    'Ensure proper documentation of care plan modifications'
  ];

  const data: BatchItem[] = [];

  for (let i = 0; i < 150; i++) {
    const client = clients[Math.floor(Math.random() * clients.length)];
    const clinician = clinicians[Math.floor(Math.random() * clinicians.length)];
    const auditor = auditors[Math.floor(Math.random() * auditors.length)];
    const goalType = goalTypes[Math.floor(Math.random() * goalTypes.length)];
    const goalSummary = goalSummaries[Math.floor(Math.random() * goalSummaries.length)];
    
    // Generate confidence with bias toward higher values for AI suggestions
    const confidence = Math.floor(Math.random() * 30) + 70; // 70-100%
    
    // AI suggestion based on confidence (higher confidence = more likely compliant)
    const aiSuggestion: 'Compliant' | 'Deficient' = confidence >= 85 && Math.random() > 0.3 ? 'Compliant' : 'Deficient';
    
    // Status distribution
    const statusRand = Math.random();
    let status: BatchItem['status'];
    if (statusRand < 0.6) status = 'Pending';
    else if (statusRand < 0.75) status = 'Auto-Approved';
    else if (statusRand < 0.85) status = 'Auto-Rejected';
    else if (statusRand < 0.95) status = 'Approved';
    else status = 'Rejected';

    // Generate visit date within last 30 days
    const visitDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);

    data.push({
      id: `batch-${i + 1}`,
      clientId: client.id,
      clientName: client.name,
      clinicianId: clinician.id,
      clinicianName: clinician.name,
      auditorName: auditor,
      visitDate: visitDate.toLocaleDateString(),
      goalSummary,
      goalType,
      aiSuggestion,
      confidence,
      status,
      lastModified: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      expandedContent: `Detailed clinical notes for ${goalSummary.toLowerCase()}. Client presented with relevant symptoms and clinical indicators that support the AI evaluation. Documentation includes comprehensive assessment findings and treatment recommendations.`
    });
  }

  return data.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
};

export function BatchApproval() {
  const { setCurrentView, setSelectedAuditData } = useContext(AppContext);
  const [batchData] = useState<BatchItem[]>(generateMockBatchData());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<BatchFilters>({
    confidenceMin: 70,
    confidenceMax: 100,
    clinician: '',
    auditor: '',
    status: ['Pending'],
    aiSuggestion: [],
    searchQuery: ''
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'approve' | 'reject' | 'skip';
    items: string[];
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Filter data based on current filters
  const filteredData = useMemo(() => {
    return batchData.filter(item => {
      // Confidence filter
      if (item.confidence < filters.confidenceMin || item.confidence > filters.confidenceMax) {
        return false;
      }

      // Clinician filter
      if (filters.clinician && !item.clinicianName.toLowerCase().includes(filters.clinician.toLowerCase())) {
        return false;
      }

      // Auditor filter
      if (filters.auditor && item.auditorName !== filters.auditor) {
        return false;
      }

      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(item.status)) {
        return false;
      }

      // AI suggestion filter
      if (filters.aiSuggestion.length > 0 && !filters.aiSuggestion.includes(item.aiSuggestion)) {
        return false;
      }

      // Search query filter
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        return (
          item.clientName.toLowerCase().includes(query) ||
          item.goalSummary.toLowerCase().includes(query) ||
          item.clientId.toLowerCase().includes(query) ||
          item.clinicianName.toLowerCase().includes(query) ||
          item.auditorName.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [batchData, filters]);

  // Calculate metrics from filtered data
  const metrics = useMemo(() => {
    const totalItems = filteredData.length;
    
    // Pending items
    const pendingItems = filteredData.filter(item => item.status === 'Pending').length;
    
    // Auto-approval rate
    const autoApprovedItems = filteredData.filter(item => 
      item.status === 'Auto-Approved' || item.status === 'Auto-Rejected'
    ).length;
    const autoApprovalRate = totalItems > 0 ? (autoApprovedItems / totalItems) * 100 : 0;
    
    // Average confidence score
    const avgConfidence = totalItems > 0 
      ? filteredData.reduce((sum, item) => sum + item.confidence, 0) / totalItems 
      : 0;
    
    // High priority items (low confidence or deficient)
    const highPriorityItems = filteredData.filter(item => 
      item.confidence < 80 || item.aiSuggestion === 'Deficient'
    ).length;

    // Calculate trends (mock data for demonstration)
    // In a real application, you would compare with previous period data
    const pendingTrend = {
      percentage: Math.random() * 20 - 10, // Random -10% to +10%
      isPositive: pendingItems < (totalItems * 0.6), // Good if pending is less than 60%
      hasData: true,
    };

    const autoApprovalTrend = {
      percentage: Math.random() * 15 + 5, // Random 5% to 20% increase
      isPositive: true, // Higher auto-approval is generally good
      hasData: true,
    };

    const confidenceTrend = {
      percentage: Math.random() * 10 + 2, // Random 2% to 12% increase
      isPositive: avgConfidence >= 85, // Good if confidence is high
      hasData: true,
    };

    const priorityTrend = {
      percentage: Math.random() * 15 - 7.5, // Random -7.5% to +7.5%
      isPositive: highPriorityItems < (totalItems * 0.2), // Good if priority items are less than 20%
      hasData: true,
    };

    return {
      pendingItems,
      autoApprovalRate: autoApprovalRate.toFixed(1),
      avgConfidence: avgConfidence.toFixed(1),
      highPriorityItems,
      pendingTrend,
      autoApprovalTrend,
      confidenceTrend,
      priorityTrend,
    };
  }, [filteredData]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const selectedCount = selectedItems.size;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelection = new Set(selectedItems);
      paginatedData.forEach(item => newSelection.add(item.id));
      setSelectedItems(newSelection);
    } else {
      const newSelection = new Set(selectedItems);
      paginatedData.forEach(item => newSelection.delete(item.id));
      setSelectedItems(newSelection);
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelection = new Set(selectedItems);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    setSelectedItems(newSelection);
  };

  const handleBulkAction = (action: 'approve' | 'reject' | 'skip') => {
    if (selectedItems.size === 0) return;

    setPendingAction({
      type: action,
      items: Array.from(selectedItems)
    });
    setShowConfirmDialog(true);
  };

  const confirmBulkAction = () => {
    if (!pendingAction) return;

    const actionText = pendingAction.type === 'approve' ? 'approved' : 
                     pendingAction.type === 'reject' ? 'rejected' : 'skipped';

    // Simulate API call
    setTimeout(() => {
      toast.success(`Successfully ${actionText} ${pendingAction.items.length} items`);
      setSelectedItems(new Set());
      setShowConfirmDialog(false);
      setPendingAction(null);
    }, 500);
  };

  // Handle row click navigation to Audit Goals Detail
  const handleRowClick = (item: BatchItem) => {
    // Convert BatchItem to format expected by AuditGoalsDetailView
    const auditData = {
      auditId: `AUDIT-${item.id.toUpperCase()}`,
      clientName: item.clientName,
      clientId: item.clientId,
      clinicianName: item.clinicianName,
      clinicianId: item.clinicianId,
      department: item.goalType, // Using goalType as department
      visitDate: item.visitDate,
      visitReason: item.goalSummary,
      auditorName: item.auditorName,
      reviewTime: (Math.random() * 2 + 0.5).toFixed(1),
      confidence: item.confidence,
      goalCount: Math.floor(Math.random() * 10) + 5, // 5-15 goals
      // Additional fields that might be useful
      originalBatchItem: item,
      source: 'batch-approval'
    };

    setSelectedAuditData(auditData);
    setCurrentView('audit-goals-detail');
  };

  const isAllSelected = paginatedData.length > 0 && paginatedData.every(item => selectedItems.has(item.id));
  const isIndeterminate = paginatedData.some(item => selectedItems.has(item.id)) && !isAllSelected;

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        {/* Breadcrumb Navigation */}
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage style={{ fontSize: '11.2px' }}>Audit</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                Audit
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Process multiple audit items efficiently with bulk actions. Click any row to view detailed audit goals.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="px-3 py-1">
                {filteredData.length} items available
              </Badge>
              {selectedCount > 0 && (
                <Badge className="px-3 py-1">
                  {selectedCount} selected
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Metrics Section */}
        <div className="px-8 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Pending Review"
              value={metrics.pendingItems.toLocaleString()}
              icon={Clock}
              trend={metrics.pendingTrend}
              description="Items waiting for manual review"
            />
            <MetricCard
              title="Auto-Approval Rate"
              value={`${metrics.autoApprovalRate}%`}
              icon={Zap}
              trend={metrics.autoApprovalTrend}
              description="Percentage of items processed automatically"
            />
            <MetricCard
              title="Avg AI Confidence"
              value={`${metrics.avgConfidence}%`}
              icon={Target}
              trend={metrics.confidenceTrend}
              description="Average confidence score across all items"
            />
            <MetricCard
              title="High Priority Items"
              value={metrics.highPriorityItems.toLocaleString()}
              icon={TrendingUp}
              trend={metrics.priorityTrend}
              description="Items requiring immediate attention"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="px-8 pb-6">
          <BatchFilters
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>

        {/* Action Bar */}
        {selectedCount > 0 && (
          <div className="px-8 pb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-blue-900" style={{ fontSize: '11.2px', fontWeight: 500 }}>
                  {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => handleBulkAction('approve')}
                  className="bg-green-600 hover:bg-green-700"
                  style={{ fontSize: '9.6px' }}
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Approve Selected
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleBulkAction('reject')}
                  style={{ fontSize: '9.6px' }}
                >
                  <X className="w-3 h-3 mr-1" />
                  Reject Selected
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('skip')}
                  style={{ fontSize: '9.6px' }}
                >
                  <SkipForward className="w-3 h-3 mr-1" />
                  Skip Selected
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedItems(new Set())}
                  style={{ fontSize: '9.6px' }}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 px-8 pb-8">
          <BatchTable
            data={paginatedData}
            selectedItems={selectedItems}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
            onRowClick={handleRowClick}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Confirmation Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Confirm Bulk Action
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingAction && (
                  <>
                    You are about to <strong>{pendingAction.type}</strong> {pendingAction.items.length} item{pendingAction.items.length !== 1 ? 's' : ''}. 
                    {pendingAction.type === 'approve' && ' This will mark them as compliant and they will not require further review.'}
                    {pendingAction.type === 'reject' && ' This will mark them as non-compliant and may require follow-up actions.'}
                    {pendingAction.type === 'skip' && ' This will move them to a later review queue.'}
                    <br /><br />
                    This action cannot be easily undone. Are you sure you want to continue?
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmBulkAction}>
                Yes, {pendingAction?.type || 'Continue'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}